import { DEFAULT_SHORTCUT_BINDINGS } from "./defaultBindings";

const TEXT_LIKE_INPUT_TYPES = new Set([
  "",
  "text",
  "search",
  "url",
  "tel",
  "password",
  "email",
  "number",
]);

function normalizeKey(key) {
  const value = String(key || "");
  if (!value) return "";
  if (value === " ") return "space";
  if (value === "Esc") return "escape";
  return value.toLowerCase();
}

function normalizeCombo(combo) {
  return String(combo || "")
    .split("+")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .join("+");
}

/** Keys pressed on the numeric keypad share `event.key` with the main row; match `event.code` instead. */
const NUMPAD_KEY_CODE_BY_TOKEN = {
  numpad0: "Numpad0",
  numpad1: "Numpad1",
  numpad2: "Numpad2",
  numpad3: "Numpad3",
  numpad4: "Numpad4",
  numpad5: "Numpad5",
  numpad6: "Numpad6",
  numpad7: "Numpad7",
  numpad8: "Numpad8",
  numpad9: "Numpad9",
  numpaddecimal: "NumpadDecimal",
  numpadenter: "NumpadEnter",
};

function parseCombo(combo) {
  const tokens = new Set(normalizeCombo(combo).split("+").filter(Boolean));
  let key = "";
  for (const token of tokens) {
    if (
      token !== "mod" &&
      token !== "ctrl" &&
      token !== "meta" &&
      token !== "alt" &&
      token !== "shift"
    ) {
      key = token;
      break;
    }
  }
  return {
    key,
    numpadCode: NUMPAD_KEY_CODE_BY_TOKEN[key] || "",
    mod: tokens.has("mod"),
    ctrl: tokens.has("ctrl"),
    meta: tokens.has("meta"),
    alt: tokens.has("alt"),
    shift: tokens.has("shift"),
  };
}

function matchesCombo(event, combo) {
  const parsed = parseCombo(combo);
  if (!parsed.key) return false;
  if (parsed.numpadCode) {
    if (event.code !== parsed.numpadCode) return false;
  } else if (normalizeKey(event.key) !== parsed.key) return false;

  const modPressed = !!(event.ctrlKey || event.metaKey);
  if (parsed.mod !== modPressed) return false;
  if (!parsed.mod) {
    if (parsed.ctrl !== !!event.ctrlKey) return false;
    if (parsed.meta !== !!event.metaKey) return false;
  }
  if (parsed.alt !== !!event.altKey) return false;
  if (parsed.shift !== !!event.shiftKey) return false;
  return true;
}

function isEditableTarget(target) {
  if (!target || typeof target !== "object") return false;
  const editableElement = target.closest?.(
    "input, textarea, select, [contenteditable='true'], [contenteditable='']"
  );
  if (!editableElement) return false;
  if (editableElement.isContentEditable) return true;
  const tagName = editableElement.tagName ? String(editableElement.tagName).toUpperCase() : "";
  if (tagName === "TEXTAREA") return true;
  if (tagName === "SELECT") return true;
  if (tagName !== "INPUT") return false;
  const inputType = String(editableElement.getAttribute?.("type") || "").toLowerCase();
  return TEXT_LIKE_INPUT_TYPES.has(inputType);
}

class ShortcutManager {
  constructor() {
    this.bindings = { ...DEFAULT_SHORTCUT_BINDINGS };
    this.handlers = new Map();
    this.listenerAttached = false;
    this.listenerRefCount = 0;
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  attach() {
    this.listenerRefCount += 1;
    if (this.listenerAttached || typeof document === "undefined") return;
    document.addEventListener("keydown", this._onKeyDown);
    this.listenerAttached = true;
  }

  detach() {
    this.listenerRefCount = Math.max(0, this.listenerRefCount - 1);
    if (this.listenerRefCount > 0) return;
    if (!this.listenerAttached || typeof document === "undefined") return;
    document.removeEventListener("keydown", this._onKeyDown);
    this.listenerAttached = false;
  }

  registerAction(actionId, handler, options = {}) {
    if (!actionId || typeof handler !== "function") {
      return () => {};
    }
    const entry = {
      handler,
      enabled: options.enabled !== false,
      allowInEditable: options.allowInEditable === true,
      preventDefault: options.preventDefault !== false,
      stopPropagation: options.stopPropagation === true,
      priority: Number.isFinite(options.priority) ? options.priority : 0,
    };
    const existing = this.handlers.get(actionId) || [];
    this.handlers.set(actionId, [...existing, entry]);
    return () => {
      const current = this.handlers.get(actionId) || [];
      const next = current.filter((item) => item !== entry);
      if (next.length === 0) {
        this.handlers.delete(actionId);
      } else {
        this.handlers.set(actionId, next);
      }
    };
  }

  _onKeyDown(event) {
    const actionIds = Object.keys(this.bindings);
    for (const actionId of actionIds) {
      const combos = this.bindings[actionId] || [];
      if (!combos.some((combo) => matchesCombo(event, combo))) continue;

      const candidates = [...(this.handlers.get(actionId) || [])].sort(
        (a, b) => b.priority - a.priority
      );
      for (const candidate of candidates) {
        if (!candidate.enabled) continue;
        if (!candidate.allowInEditable && isEditableTarget(event.target)) continue;
        const handled = candidate.handler(event);
        if (handled === false) continue;
        if (candidate.preventDefault) event.preventDefault();
        if (candidate.stopPropagation) event.stopPropagation();
        return;
      }
    }
  }
}

export const shortcutManager = new ShortcutManager();
